/**
 * Текстовое поле формы — замена сырым <input> для text/password/email/date/time.
 * Отклонения (иконки, паддинги, цвета админки) передаются через className и
 * перекрывают базу через twMerge.
 * type="time" рисует иконку часов слева вместо нативного индикатора браузера.
 *
 * Внешний вид по умолчанию — «утопленное» поле дизайн-системы (DESIGN_SYSTEM.md,
 * раздел 7): фон --rla-sunk, никакой видимой рамки в покое, фокус подсвечивается
 * внутренним бренд-кольцом. Прежняя база «белое поле + border-gray-300» — старый
 * язык; он остался под variant="outline" для админки, у которой своя нейтральная
 * палитра (border-stone-200 / border-border) и которая на .rla-* не переводится.
 *
 * variant="ghost" — белое поле без рамки для онбординга/входа (референс №19/№21).
 */
import type {InputHTMLAttributes} from "react";

import {Clock} from "lucide-react";

import {cn} from "../core/utils";

/** Утопленное поле приложения / белое поле с рамкой (админка) / белое поле без рамки (онбординг). */
export type FieldVariant = "sunk" | "outline" | "ghost";

/** Материал утопленного поля: фон, чернила, бренд-кольцо на фокусе. */
export const SUNK_FIELD =
    "rounded-[var(--rla-r-row)] bg-[var(--rla-sunk)] text-[var(--rla-ink)] " +
    "transition-[background-color,box-shadow] duration-150 " +
    "focus:bg-white focus:shadow-[inset_0_0_0_1.6px_rgba(251,44,54,0.5)]";

/** Белое поле без рамки для онбординга: белый фон, бренд-кольцо на фокусе (референс №19/№21). */
const GHOST_FIELD =
    "rounded-[var(--rla-r-row)] bg-white text-[var(--rla-ink)] " +
    "transition-[background-color,box-shadow] duration-150 " +
    "focus:shadow-[inset_0_0_0_1.6px_rgba(251,44,54,0.5)]";

/** Плавающий список подсказок: белая поверхность со ступенью E1 и волоском. */
export const SUNK_POPUP =
    "rounded-[var(--rla-r-inner)] bg-[var(--rla-surface)] shadow-[var(--rla-e0),var(--rla-e1)]";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
    /** md — компактные поля (py-2), lg — крупные поля страниц/модалок (py-3). */
    size?: "md" | "lg";
    /** Подсветить ошибку. */
    error?: boolean;
    /** sunk — язык приложения (по умолчанию), outline — прежнее белое поле с рамкой, ghost — белое поле без рамки (онбординг). */
    variant?: FieldVariant;
}

export function Input({
    size = "md",
    error = false,
    variant = "sunk",
    className,
    type = "text",
    placeholder,
    ...props
}: InputProps) {
    const sunk = variant === "sunk";
    const ghost = variant === "ghost";

    const input = (
        <input
            type={type}
            className={cn(
                "w-full px-4 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                sunk
                    ? cn(SUNK_FIELD, "placeholder:text-[var(--rla-ink-3)]")
                    : ghost
                        ? cn(GHOST_FIELD, "placeholder:text-[var(--rla-ink-3)]")
                        : "rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-red-500",
                size === "md" ? "py-2" : "py-3",
                error && (sunk
                    ? "shadow-[inset_0_0_0_1.6px_var(--rla-alarm)]"
                    : ghost
                        ? "shadow-[inset_0_0_0_1.6px_var(--rla-alarm)]"
                        : "border-red-500"
                ),
                type === "time" && "pl-11 [&::-webkit-calendar-picker-indicator]:hidden",
                className,
            )}
            {...props}
        />
    );

    if (type !== "time") return input;

    return (
        <div className="relative">
            <Clock
                className={cn(
                    "pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2",
                    sunk ? "text-[var(--rla-ink-3)]" : "text-gray-400",
                )}
            />
            {input}
        </div>
    );
}